import { create } from 'zustand';
import { queryClient } from '@/lib/queryClient';
import type { User } from '@/api/types';

const SESSION_ACTIVE_COMPETITION_KEY = 'aperture.session.activeCompetitionId';
/** Shared by all tabs: "this browser has a refresh cookie", so signed-out visitors skip a pointless /auth/refresh. */
const HAS_SESSION_KEY = 'aperture.hasSession';
const AUTH_CHANNEL = 'aperture-auth';

type AuthMessage = { type: 'login'; userId: string } | { type: 'logout' };

const channel: BroadcastChannel | null = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(AUTH_CHANNEL);

function setHasSession(value: boolean) {
  try {
    if (value) localStorage.setItem(HAS_SESSION_KEY, '1');
    else localStorage.removeItem(HAS_SESSION_KEY);
  } catch {
    // storage unavailable - the refresh just runs on every load.
  }
}

function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(HAS_SESSION_KEY) === '1';
  } catch {
    return true;
  }
}

/**
 * Auth state.
 *
 * The access token lives only in memory (this store). The refresh token is an
 * httpOnly cookie set by the backend: JavaScript never sees it, and because a
 * cookie is shared by every tab, opening the app in a new tab redeems it via
 * /auth/refresh and the user is signed in there too. Sign-in and sign-out are
 * announced to the other tabs over a BroadcastChannel so they follow along.
 */
interface AuthState {
  accessToken: string | null;
  user: User | null;
  isHydrated: boolean;
  /** The competition this tab is working in (per tab on purpose: two tabs can show two competitions). */
  activeCompetitionId: string | null;
  setSession: (session: { accessToken: string; user: User }) => void;
  setAccessToken: (accessToken: string) => void;
  setActiveCompetitionId: (id: string | null) => void;
  /** `broadcast: false` when this call is itself the reaction to another tab's sign-out. */
  clearSession: (options?: { broadcast?: boolean }) => void;
  hydrate: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  accessToken: null,
  user: null,
  isHydrated: false,
  activeCompetitionId: null,

  setSession: ({ accessToken, user }) => {
    // React Query's cache keys (e.g. ['judging', 'my-competitions']) don't
    // include the calling user's id - they rely on the server scoping
    // results to whoever the access token belongs to. If a DIFFERENT account
    // signs in afterward, TanStack Query would otherwise keep serving the
    // previous user's cached results as "fresh" for up to staleTime. Wipe the
    // cache on every actual account switch; a plain token refresh (same user)
    // is deliberately excluded so it doesn't cause needless refetch flicker.
    const previous = get().user;
    const isAccountSwitch = Boolean(previous && previous.id !== user.id);
    if (isAccountSwitch) {
      queryClient.clear();
      try {
        // A competition selected under a previous account must not leak into this one.
        sessionStorage.removeItem(SESSION_ACTIVE_COMPETITION_KEY);
      } catch {
        // ignore
      }
    }
    setHasSession(true);
    if (!previous || isAccountSwitch) channel?.postMessage({ type: 'login', userId: user.id } satisfies AuthMessage);
    set({ accessToken, user, activeCompetitionId: isAccountSwitch ? null : get().activeCompetitionId });
  },

  setAccessToken: (accessToken) => set({ accessToken }),

  setActiveCompetitionId: (id) => {
    try {
      if (id) sessionStorage.setItem(SESSION_ACTIVE_COMPETITION_KEY, id);
      else sessionStorage.removeItem(SESSION_ACTIVE_COMPETITION_KEY);
    } catch {
      // ignore
    }
    set({ activeCompetitionId: id });
  },

  clearSession: (options) => {
    queryClient.clear();
    try {
      sessionStorage.removeItem(SESSION_ACTIVE_COMPETITION_KEY);
    } catch {
      // ignore
    }
    setHasSession(false);
    if (options?.broadcast !== false) channel?.postMessage({ type: 'logout' } satisfies AuthMessage);
    set({ accessToken: null, user: null, activeCompetitionId: null });
  },

  hydrate: async () => {
    let activeCompetitionId: string | null = null;
    try {
      activeCompetitionId = sessionStorage.getItem(SESSION_ACTIVE_COMPETITION_KEY);
    } catch {
      // ignore
    }
    if (hasSessionHint()) {
      // Imported lazily: the API client imports this store.
      const { refreshSession } = await import('@/api/client');
      if (await refreshSession()) {
        set({ activeCompetitionId });
      }
    }
    set({ isHydrated: true });
  },
}));

channel?.addEventListener('message', (event: MessageEvent<AuthMessage>) => {
  const message = event.data;
  const { user } = useAuthStore.getState();
  if (message.type === 'logout') {
    // Don't trust the message blindly: someone may have signed in again since it was sent
    // (e.g. a stale tab announcing its dead session after you signed in with another account).
    // The cookie decides: a dead cookie signs this tab out, a live one keeps/switches the session.
    if (user) void import('@/api/client').then(({ refreshSession }) => refreshSession());
  } else if (message.type === 'login' && user?.id !== message.userId) {
    // Another account signed in elsewhere: this tab's cookie now belongs to them, so start over.
    window.location.reload();
  }
});

export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}
